<template>
  <div class="form-outline">
    <label class="label">
      <span v-if="!!label">{{ label }}</span>
      <span v-else>{{ $t('general.shared.holdedAccountNumber') }}</span>
    </label>

    <v-select
      v-model="localValue"
      :clearable="clearable"
      :clear-search-on-select="true"
      :disabled="isDisabled"
      :filterable="false"
      :options="accounts"
      :reduce="(account: any) => account.number"
      class="select-search"
      label="name"
      @search="onSearch"
      @update:model-value="$emit('update:modelValue', localValue)"
    >
      <template v-slot:no-options="{ search }">
        <div v-if="!search" class="my-4">{{ $t('general.shared.holdedAccountSearchHint') }}</div>
        <div v-else-if="creating" class="my-4">{{ $t('general.shared.creatingAccount') }}</div>
        <div v-else class="my-4">
          <v-button
            class="btn-icon h-fit fw-normal shadow-none py-0 px-16"
            size="sm"
            variant="link"
            @click="createAccount(search)"
          >
            <v-icon icon="plus" size="xs" space="me-8" />
            {{ $t('general.shared.createHoldedAccount', { name: accountName || search, number: suggestedNumber ? ` (${suggestedNumber})` : '' }) }}
          </v-button>
        </div>
      </template>

      <template v-slot:option="option">
        <div class="d-center">
          <span>{{ formatLabel(option) }}</span>
        </div>
      </template>

      <template v-slot:selected-option="option">
        <div class="selected d-center py-8">
          <span>{{ formatLabel(option) }}</span>
        </div>
      </template>

      <template #open-indicator="{ attributes }">
        <span v-bind="attributes">
          <v-icon icon="search" size="sm" />
        </span>
      </template>
    </v-select>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import vSelect from 'vue-select';
import api from '@/api';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';

export default defineComponent({
  name: 'VSelectHoldedAccountSearch',
  components: {
    VIcon,
    vSelect,
    VButton,
  },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: [String, Number],
      required: false,
      default: null,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    accountName: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      localValue: this.modelValue != null ? String(this.modelValue) : null,
      accounts: [] as { id: string; number: string; name: string }[],
      creating: false as boolean,
      suggestedNumber: null as string | null,
    };
  },
  watch: {
    modelValue(newValue) {
      this.localValue = newValue != null ? String(newValue) : null;
    },
  },
  methods: {
    formatLabel(option: any): string {
      if (typeof option === 'string') {
        const found = this.accounts.find((account) => account.number === option);

        return found && found.name ? `${found.name} (${found.number})` : option;
      }

      if (option?.name && option?.number) {
        return `${option.name} (${option.number})`;
      }

      if (option?.number != null) {
        return String(option.number);
      }

      if (option?.name) {
        return option.name;
      }

      return '';
    },
    onSearch(search: string, loading: any) {
      if (search.length) {
        loading(true);
        this.loadAccounts(loading, search);
      }
    },
    async loadAccounts(loading?: any, search?: string) {
      try {
        const response = await api.supplier.searchHoldedAccounts(search || '');

        this.accounts = (response?.content || []).map((account: any) => ({
          ...account,
          number: String(account.number),
        }));

        if (this.accounts.length === 0) {
          const isNumber = /^4000\d{4}$/.test(search || '');
          if (isNumber) {
            this.suggestedNumber = search || null;
          } else {
            await this.suggestNumber();
          }
        } else {
          this.suggestedNumber = null;
        }
      } catch (e: any) {
        console.error(e);
        this.$toast.error(e.response?.data?.message);
      } finally {
        if (!!loading) {
          loading(false);
        }
      }
    },
    async suggestNumber() {
      try {
        const response = await api.supplier.getNextHoldedAccountNumber();

        this.suggestedNumber = response?.content?.number || null;
      } catch (e: any) {
        console.error(e);
        this.suggestedNumber = null;
      }
    },
    async createAccount(search: string) {
      try {
        this.creating = true;

        const isNumber = /^4000\d{4}$/.test(search);
        const number = isNumber ? search : (this.suggestedNumber || undefined);
        const name = this.accountName || search;

        if (!name) {
          this.$toast.error(this.$t('general.shared.holdedAccountNameRequired'));
          return;
        }

        const response = await api.supplier.createHoldedAccount(name, number);

        if (!response?.content) {
          throw new Error('Holded account creation failed');
        }

        const account = response.content;

        this.accounts = [{
          ...account,
          number: String(account.number),
        }];
        this.localValue = String(account.number);

        this.$emit('update:modelValue', this.localValue);
        this.$toast.success(this.$t('general.shared.holdedAccountCreated', { number: account.number }));
      } catch (e: any) {
        console.error(e);
        this.$toast.error(e.response?.data?.message || e.message);
      } finally {
        this.creating = false;
      }
    },
  },
});
</script>
